// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Adding or editing one line of a draft BOM (design doc §7.3, §6.2.1).
 *
 * One dialog for both, because the fields are the same and the difference is a PATCH
 * instead of a POST. The parent is chosen from the lines that already exist rather than
 * typed: a position code that does not resolve is a rejected save the author has to
 * decode, and the list is right there.
 *
 * The quantity field says what it means. `quantity` is usage **per parent**, and the number
 * a build actually needs is that multiplied through every ancestor — the distinction the
 * design doc spends §6.2.1 on, and the one that decides how many serials an operator will
 * be asked to scan. A line entered with the whole-machine figure looks right and produces
 * twice the slots.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { BomItem, ModuleType } from "@/api/types";
import { moduleTypeLabel } from "./BomModulesSummary";
import { Modal } from "./Modal";

export interface BomItemDraft {
  position_code: string;
  material_info: string;
  part_name: string;
  drawing_no: string;
  parent_item_id: string;
  material_code: string;
  quantity: number;
  is_critical: boolean;
  remark: string;
  /** An assembly line's module type (component design §4.5); "" for none. */
  module_type: string;
}

function toDraft(item: BomItem | null): BomItemDraft {
  return {
    position_code: item?.position_code ?? "",
    material_info: item?.material_info ?? "",
    part_name: item?.part_name ?? "",
    drawing_no: item?.drawing_no ?? "",
    parent_item_id: item?.parent_item_id ?? "",
    material_code: item?.material_code ?? "",
    quantity: item?.quantity ?? 1,
    is_critical: item?.is_critical ?? false,
    remark: item?.remark ?? "",
    module_type: item?.module_type ?? "",
  };
}

interface Props {
  /** The line being edited, or null when adding. */
  item: BomItem | null;
  /** Every line in the draft, for the parent picker. */
  items: BomItem[];
  /** Names the language the remark box writes in — the one the BOM page is showing. */
  remarkLabel: string;
  pending: boolean;
  error: React.ReactNode;
  onSave: (draft: BomItemDraft) => void;
  onClose: () => void;
  /** The module SN letters, offered on a line that has lines under it. */
  moduleTypes?: ModuleType[];
}

export function BomItemDialog({
  item,
  items,
  remarkLabel,
  pending,
  error,
  onSave,
  onClose,
  moduleTypes = [],
}: Props) {
  const { t, i18n } = useTranslation();
  // Only an assembly — a line with lines under it — can be a module (§4.3).
  const isAssembly = Boolean(item && items.some((other) => other.parent_item_id === item.id));
  const [draft, setDraft] = useState(() => toDraft(item));

  // Its own subtree is excluded as well as itself: the server refuses a cycle, but offering
  // the choice at all invites a save that cannot succeed. Iterated to a fixed point rather
  // than walked once, because the lines arrive in sort order, not parent-before-child.
  const blocked = useMemo(() => {
    const found = new Set<string>();
    if (!item) return found;
    found.add(item.id);
    for (let grew = true; grew; ) {
      grew = false;
      for (const candidate of items) {
        const parent = candidate.parent_item_id;
        if (parent && found.has(parent) && !found.has(candidate.id)) {
          found.add(candidate.id);
          grew = true;
        }
      }
    }
    return found;
  }, [item, items]);

  const canSave =
    draft.position_code.trim().length > 0 &&
    draft.material_info.trim().length > 0 &&
    draft.quantity >= 1 &&
    !pending;

  return (
    <Modal
      title={item ? t("bom.editItem") : t("bom.addItem")}
      onClose={onClose}
      onSubmit={() => onSave(draft)}
      footer={
        <>
          <button type="submit" className="button-primary" disabled={!canSave}>
            {t("common.save")}
          </button>
          <button type="button" onClick={onClose}>
            {t("common.cancel")}
          </button>
        </>
      }
    >
      <div className="form-row">
        <label className="required" htmlFor="item-code">
          {t("bom.positionCode")}
        </label>
        <input
          id="item-code"
          className="mono"
          required
          maxLength={128}
          value={draft.position_code}
          onChange={(event) => setDraft({ ...draft, position_code: event.target.value })}
        />
      </div>
      <div className="form-row">
        <label className="required" htmlFor="item-name">
          {t("bom.positionName")}
        </label>
        <input
          id="item-name"
          required
          maxLength={255}
          value={draft.material_info}
          onChange={(event) => setDraft({ ...draft, material_info: event.target.value })}
        />
      </div>
      <div className="form-row">
        <label htmlFor="item-part-name">{t("bom.partName")}</label>
        <input
          id="item-part-name"
          maxLength={255}
          placeholder={t("bom.partNamePlaceholder")}
          value={draft.part_name}
          onChange={(event) => setDraft({ ...draft, part_name: event.target.value })}
        />
      </div>
      {isAssembly && (
        <div className="form-row">
          <label htmlFor="item-module-type">{t("bom.moduleType")}</label>
          <select
            id="item-module-type"
            value={draft.module_type}
            onChange={(event) => setDraft({ ...draft, module_type: event.target.value })}
          >
            <option value="">{t("bom.noModule")}</option>
            {moduleTypes
              .filter((type) => type.status === "ACTIVE" || type.code === draft.module_type)
              .map((type) => (
                <option key={type.code} value={type.code}>
                  {moduleTypeLabel(type.code, moduleTypes, i18n.language === "en-US")}
                </option>
              ))}
          </select>
        </div>
      )}
      <div className="form-row">
        <label htmlFor="item-drawing-no">{t("bom.drawingNo")}</label>
        <input
          id="item-drawing-no"
          className="mono"
          maxLength={128}
          value={draft.drawing_no}
          onChange={(event) => setDraft({ ...draft, drawing_no: event.target.value })}
        />
      </div>
      <div className="form-row">
        <label htmlFor="item-parent">{t("bom.parent")}</label>
        <select
          id="item-parent"
          value={draft.parent_item_id}
          onChange={(event) => setDraft({ ...draft, parent_item_id: event.target.value })}
        >
          <option value="">{t("bom.noParent")}</option>
          {items
            .filter((candidate) => !blocked.has(candidate.id))
            .map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.position_code} · {candidate.material_info}
              </option>
            ))}
        </select>
      </div>
      <div className="form-row">
        <label className="required" htmlFor="item-quantity">
          {t("bom.quantity")}
        </label>
        <input
          id="item-quantity"
          type="number"
          min={1}
          required
          value={draft.quantity}
          onChange={(event) => setDraft({ ...draft, quantity: Number(event.target.value) })}
        />
      </div>
      <div className="form-row">
        <span className="form-row-spacer" />
        <small className="hint">{t("bom.quantityHint")}</small>
      </div>
      <div className="form-row">
        <label htmlFor="item-material-code">{t("bom.materialCode")}</label>
        <input
          id="item-material-code"
          className="mono"
          maxLength={128}
          value={draft.material_code}
          onChange={(event) => setDraft({ ...draft, material_code: event.target.value })}
        />
      </div>
      <div className="form-row">
        <label htmlFor="item-critical">{t("bom.critical")}</label>
        <input
          id="item-critical"
          type="checkbox"
          checked={draft.is_critical}
          onChange={(event) => setDraft({ ...draft, is_critical: event.target.checked })}
        />
      </div>
      <div className="form-row">
        <span className="form-row-spacer" />
        <small className="hint">{t("bom.criticalHint")}</small>
      </div>
      <div className="form-row">
        <label htmlFor="item-remark">{remarkLabel}</label>
        {/* An input like every other field in this dialog: a remark is a sentence or two,
            and a textarea here was the one field that looked and sized differently. */}
        <input
          id="item-remark"
          maxLength={500}
          value={draft.remark}
          onChange={(event) => setDraft({ ...draft, remark: event.target.value })}
        />
      </div>
      <div className="form-row">
        <span className="form-row-spacer" />
        <small className="hint">{t("bom.remarkHint")}</small>
      </div>
      {error}
    </Modal>
  );
}
